import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePOS } from "@/context/POSContext";
import * as admin from "@/lib/adminApi";

/**
 * Order lists (settings sweep X5, 2026-10-19).
 *
 * Four lists the server could already add to and edit, with no screen: payment
 * modes, cancellation reasons, order types (the settings picker only toggled them)
 * and the order workflow — which, since 2026-10-13, also decides who may cancel
 * items and tables. Every write goes to the list's own endpoint and is shown only
 * once the server answers; a refusal is shown by name.
 */

const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] || {};
    const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : null;
    return `${field ? `${String(field).replaceAll("_", " ")}: ` : ""}${first.msg || "Request rejected"}`;
  }
  return failure?.message || "Could not reach the server";
};

const TABS = [
  ["payment", "Payment modes"],
  ["reasons", "Cancellation reasons"],
  ["notes", "Item notes"],
  ["types", "Order types"],
  ["workflow", "Order workflow"],
];

const APPLIES = [
  ["both", "Items and whole orders"],
  ["item", "Items only"],
  ["order", "Whole orders only"],
];

const ROLES = ["admin", "cashier", "captain"];
// Mirrors api/workflow.py: steps every bill passes through stay on.
const BILLING_PATH = new Set(["kot_sent", "billing", "closed"]);

const STATUS_LABEL = {
  open: "Open", kot_sent: "Sent to kitchen", billing: "Bill printed", closed: "Settled", cancelled: "Cancelled",
};

function useList(load, token) {
  const [state, setState] = useState({ loading: true, error: null, rows: [] });
  const reload = useCallback(() => {
    setState(s => ({ ...s, loading: true }));
    return load(token)
      .then(rows => setState({ loading: false, error: null, rows: rows || [] }))
      .catch(failure => setState({ loading: false, error: errorText(failure), rows: [] }));
  }, [load, token]);
  useEffect(() => { reload(); }, [reload]);
  return [state, reload];
}

/** One editable row: draft locally, Save sends only what changed. */
function EditRow({ row, fields, onSave, readOnly, testId }) {
  const [draft, setDraft] = useState(row);
  const [busy, setBusy] = useState(false);
  // Reset only when this row's saved data changed: a reload after saving ANOTHER
  // row hands every row a new object, which used to discard unsaved edits here.
  const rowKey = JSON.stringify(row);
  useEffect(() => { setDraft(JSON.parse(rowKey)); }, [rowKey]);
  const changed = Object.fromEntries(fields.map(f => [f.key, draft[f.key]]).filter(([k, v]) => v !== row[k]));
  const dirty = Object.keys(changed).length > 0;
  const save = async () => {
    setBusy(true);
    const ok = await onSave(row.id, changed);
    setBusy(false);
    if (!ok) setDraft(row);
  };
  return (
    <div className={`order-list-row${row.is_active === false ? " is-inactive" : ""}`} data-testid={testId}>
      {fields.map(f => (
        <div key={f.key} className={`order-list-cell order-list-cell--${f.kind}`}>
          {f.kind === "text" && f.fixedOnceSaved && (
            // Bible §8: the internal name is what reports group by; it never changes.
            // Staff-facing wording changes through "Shown as".
            <span className="order-list-fixed" title="Fixed once saved — reports use it. Change “Shown as” instead." data-testid={`${testId}-${f.key}`}>
              {row[f.key]}
            </span>
          )}
          {f.kind === "text" && !f.fixedOnceSaved && (
            <Input value={draft[f.key] ?? ""} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
              disabled={readOnly || busy} aria-label={f.label} className="h-8" data-testid={`${testId}-${f.key}`} />
          )}
          {f.kind === "number" && (
            <Input type="number" value={draft[f.key] ?? 0} onChange={e => setDraft({ ...draft, [f.key]: Number(e.target.value) })}
              disabled={readOnly || busy} aria-label={f.label} className="h-8 w-20" data-testid={`${testId}-${f.key}`} />
          )}
          {f.kind === "bool" && (
            <label className="order-list-check">
              <input type="checkbox" checked={Boolean(draft[f.key])} onChange={e => setDraft({ ...draft, [f.key]: e.target.checked })}
                disabled={readOnly || busy} data-testid={`${testId}-${f.key}`} />
              {f.label}
            </label>
          )}
          {f.kind === "select" && (
            <select value={draft[f.key]} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
              disabled={readOnly || busy} aria-label={f.label} className="h-8 rounded-md border bg-background px-2" data-testid={`${testId}-${f.key}`}>
              {f.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button variant="action" size="sm" className="h-8" disabled={!dirty || busy} onClick={save} data-testid={`${testId}-save`}>
          {busy ? "Saving…" : "Save"}
        </Button>
      )}
    </div>
  );
}

function AddRow({ fields, blank, onAdd, readOnly, testId }) {
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  if (readOnly) return null;
  const add = async () => {
    setBusy(true);
    // A name becomes the fixed id reports group by: no stray spaces in it.
    const clean = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
    const ok = await onAdd(clean);
    setBusy(false);
    if (ok) setDraft(blank);
  };
  return (
    <div className="order-list-row is-new" data-testid={testId}>
      {fields.map(f => (
        <div key={f.key} className={`order-list-cell order-list-cell--${f.kind}`}>
          {f.kind === "text" && (
            <Input value={draft[f.key] ?? ""} placeholder={f.placeholder} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
              disabled={busy} aria-label={`New ${f.label}`} className="h-8" data-testid={`${testId}-${f.key}`} />
          )}
          {f.kind === "number" && (
            <Input type="number" value={draft[f.key] ?? 0} onChange={e => setDraft({ ...draft, [f.key]: Number(e.target.value) })}
              disabled={busy} aria-label={`New ${f.label}`} className="h-8 w-20" />
          )}
          {f.kind === "bool" && (
            <label className="order-list-check">
              <input type="checkbox" checked={Boolean(draft[f.key])} onChange={e => setDraft({ ...draft, [f.key]: e.target.checked })} disabled={busy} />
              {f.label}
            </label>
          )}
          {f.kind === "select" && (
            <select value={draft[f.key]} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })} disabled={busy}
              aria-label={`New ${f.label}`} className="h-8 rounded-md border bg-background px-2">
              {f.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          )}
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-8" disabled={busy || !String(draft[fields[0].key] || "").trim()} onClick={add} data-testid={`${testId}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" />{busy ? "Adding…" : "Add"}
      </Button>
    </div>
  );
}

function ListTab({ title, hint, load, create, update, fields, blank, token, readOnly, name }) {
  const [list, reload] = useList(load, token);
  const run = async (fn, success) => {
    try {
      await fn();
      toast.success(success);
      await reload();
      return true;
    } catch (failure) {
      toast.error(`${title}: ${errorText(failure)}`);
      return false;
    }
  };
  return (
    <section className="order-list" data-testid={`order-list-${name}`}>
      <p className="order-list-hint">{hint}</p>
      {list.error && <p className="order-list-error" data-testid={`order-list-${name}-error`}>Could not load {title.toLowerCase()}: {list.error}</p>}
      {list.loading && !list.rows.length && <p className="order-list-hint">Loading…</p>}
      {list.rows.map(row => (
        <EditRow key={row.id} row={row} fields={fields} readOnly={readOnly} testId={`order-list-${name}-${row.id}`}
          onSave={(id, changes) => run(() => update(id, changes, token), "Saved")} />
      ))}
      {!list.loading && !list.error && !list.rows.length && <p className="order-list-hint">None yet.</p>}
      <AddRow fields={fields} blank={blank} readOnly={readOnly} testId={`order-list-${name}-new`}
        onAdd={draft => run(() => create(draft, token), "Added")} />
    </section>
  );
}

function WorkflowTab({ token, readOnly }) {
  const [list, reload] = useList(admin.listTransitions, token);
  const [busyId, setBusyId] = useState(null);
  const patch = async (row, body) => {
    setBusyId(row.id);
    try {
      await admin.patchTransition(row.id, body, token);
      toast.success(`${STATUS_LABEL[row.from_status] || row.from_status} → ${STATUS_LABEL[row.to_status] || row.to_status} saved`);
      await reload();
    } catch (failure) {
      toast.error(`Order workflow: ${errorText(failure)}`);
    } finally {
      setBusyId(null);
    }
  };
  const toggleRole = (row, role) => {
    const current = row.allowed_roles || [];
    // Empty means every role. Unticking one role from "everyone" leaves the other two.
    const base = current.length ? current : ROLES;
    const next = base.includes(role) ? base.filter(r => r !== role) : [...base, role];
    if (!next.length) {
      toast.error("At least one role must be allowed — switch the step off instead");
      return;
    }
    patch(row, { allowed_roles: next.length === ROLES.length ? [] : next });
  };
  return (
    <section className="order-list" data-testid="order-list-workflow">
      <p className="order-list-hint">
        Who may move an order from one state to the next. The steps into “Cancelled” also decide who may cancel items
        and whole tables. Admins are always allowed, and the steps every bill goes through cannot be switched off.
      </p>
      {list.error && <p className="order-list-error" data-testid="order-list-workflow-error">Could not load the workflow: {list.error}</p>}
      {list.rows.map(row => {
        const everyone = !(row.allowed_roles || []).length;
        const busy = busyId === row.id;
        return (
          <div key={row.id} className={`order-list-row${row.is_active ? "" : " is-inactive"}`} data-testid={`order-list-workflow-${row.id}`}>
            <div className="order-list-cell order-list-cell--text">
              <strong>{STATUS_LABEL[row.from_status] || row.from_status} → {STATUS_LABEL[row.to_status] || row.to_status}</strong>
            </div>
            <label className="order-list-check">
              <input type="checkbox" checked={row.is_active} disabled={readOnly || busy || BILLING_PATH.has(row.to_status)}
                onChange={e => patch(row, { is_active: e.target.checked })} data-testid={`order-list-workflow-${row.id}-active`} />
              Allowed
            </label>
            {ROLES.map(role => (
              <label key={role} className="order-list-check">
                <input type="checkbox" checked={everyone || role === "admin" || row.allowed_roles.includes(role)} disabled={readOnly || busy || !row.is_active || role === "admin"}
                  onChange={() => toggleRole(row, role)} data-testid={`order-list-workflow-${row.id}-${role}`} />
                {role}
              </label>
            ))}
          </div>
        );
      })}
    </section>
  );
}

export default function OrderListsPanel({ readOnly = false }) {
  const { token } = usePOS();
  const [tab, setTab] = useState("payment");
  return (
    <div className="space-y-3" data-testid="order-lists-panel">
      <div className="flex gap-1 border-b" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id}
            className={`px-3 py-2 text-meta font-semibold border-b-2 ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
            onClick={() => setTab(id)} data-testid={`order-lists-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "payment" && (
        <ListTab name="payment" title="Payment modes" token={token} readOnly={readOnly}
          hint="The tenders offered when a bill is settled. A mode that has been used is switched off rather than removed, so old bills keep their tender."
          load={admin.listPaymentModes} create={admin.createPaymentMode} update={admin.updatePaymentMode}
          blank={{ name: "", display_label: "", is_cash: false, is_active: true, sort_order: 0 }}
          fields={[
            { key: "name", kind: "text", label: "Name", placeholder: "e.g. UPI", fixedOnceSaved: true },
            { key: "display_label", kind: "text", label: "Shown as", placeholder: "Shown on screen" },
            { key: "is_cash", kind: "bool", label: "Cash (counts in the drawer)" },
            { key: "is_active", kind: "bool", label: "On" },
            { key: "sort_order", kind: "number", label: "Order" },
          ]} />
      )}
      {tab === "reasons" && (
        <ListTab name="reasons" title="Cancellation reasons" token={token} readOnly={readOnly}
          hint="The reasons staff pick from when they cancel an item or a whole table. They feed the cancellation report."
          load={admin.listCancellationReasonsAll} create={admin.createCancellationReason} update={admin.updateCancellationReason}
          blank={{ label: "", applies_to: "both", is_active: true }}
          fields={[
            { key: "label", kind: "text", label: "Reason", placeholder: "e.g. Guest changed mind" },
            { key: "applies_to", kind: "select", label: "Used for", options: APPLIES },
            { key: "is_active", kind: "bool", label: "On" },
          ]} />
      )}
      {tab === "notes" && (
        <ListTab name="notes" title="Item notes" token={token} readOnly={readOnly}
          hint="Ready-made special notes staff tap while punching a line (Extra spicy, No onion). They print on the KOT under the item; staff can also type their own. Changing one never changes a note already punched."
          load={admin.listNotePresetsAll} create={admin.createNotePreset} update={admin.updateNotePreset}
          blank={{ label: "", is_active: true, sort_order: 0 }}
          fields={[
            { key: "label", kind: "text", label: "Note", placeholder: "e.g. Less oil" },
            { key: "is_active", kind: "bool", label: "On" },
            { key: "sort_order", kind: "number", label: "Order" },
          ]} />
      )}
      {tab === "types" && (
        <ListTab name="types" title="Order types" token={token} readOnly={readOnly}
          hint="Dine-in, takeaway, delivery and any you add. “Shown as” is what staff see; the name stays fixed for reports."
          load={admin.listOrderTypesAll} create={admin.createOrderType} update={admin.updateOrderType}
          blank={{ name: "", display_label: "", requires_table: false, is_active: true, sort_order: 0 }}
          fields={[
            { key: "name", kind: "text", label: "Name", placeholder: "e.g. room_service", fixedOnceSaved: true },
            { key: "display_label", kind: "text", label: "Shown as", placeholder: "e.g. Room Service" },
            { key: "requires_table", kind: "bool", label: "Needs a table" },
            { key: "is_active", kind: "bool", label: "On" },
            { key: "sort_order", kind: "number", label: "Order" },
          ]} />
      )}
      {tab === "workflow" && <WorkflowTab token={token} readOnly={readOnly} />}
    </div>
  );
}
